/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";
modulum("UIApplicationService", ["ApplicationServiceBase", "ApplicationServiceFactory"], function (context, cls) {
  /**
   * Class responsible of managing the User Interface part
   * of the VMApplication.
   * @class UIApplicationService
   * @memberOf classes
   * @extends classes.ApplicationServiceBase
   */
  class UIApplicationService extends cls.ApplicationServiceBase /** @lends classes.UIApplicationService.prototype */ {
    constructor(app) {
      super(app);
      /** @type {?number} */
      this._currentWindowIdRef = null;
      /**
       * @type {classes.ApplicationWidget}
       */
      this._applicationWidget = cls.WidgetFactory.createWidget("Application", {
        appHash: this._application.applicationHash
      });
      this._applicationWidget.setApplicationHash(app.applicationHash);
      this._applicationWidget.onActivate(this._onActivate.bind(this));
      this._application.getSession().addApplicationWidget(app, this._applicationWidget);
      this._applicationWidget.onLayoutRequest(this._onLayoutRequest.bind(this));
      // event emitted when we want to display a new application on click
      this._applicationWidget.when(context.constants.widgetEvents.requestDisplay, this._onRequestDisplay.bind(this));
    }

    _onActivate() {
      this._application.getSession().getNavigationManager().displayApplication(this._application);
    }

    /**
     * Request display event handler executed when a switch of application is requested by user
     * @private
     */
    _onRequestDisplay() {
      const syncApplication = this.syncCurrentWindow();

      // syncApplication can be different if app is waiting for a child app
      if (syncApplication !== this._application) {
        this._applicationWidget.activate();
        syncApplication.getUI().getWidget().activate();
      }
    }

    _onLayoutRequest() {
      this._application.scheduler.layoutCommand();
    }

    destroy() {
      if (!this._destroyed) {
        this._applicationWidget.destroy();
        this._applicationWidget = null;
        super.destroy();
      }
    }
    /**
     *
     * @returns {classes.ApplicationWidget}
     */
    getWidget() {
      return this._applicationWidget;
    }

    setRunning(running) {
      if (running) {
        this.getWidget().hideWaiter();
      }
    }

    isLayoutable() {
      return this.getWidget() && this.getWidget().getElement() && !this.getWidget().getElement().classList.contains("gbc_out_of_view");
    }

    /**
     * Return the current window of this application
     * @returns {classes.WindowNode|undefined}
     */
    getCurrentWindow() {
      const windows = this.getApplication().model.getNodesByTag("Window");
      return windows.find((w) => w.getId() === this._currentWindowIdRef);
    }
    /**
     * Store the current window id,
     * then display the associated window
     * @param {number} currentWindowId
     */
    setCurrentWindow(currentWindowId) {
      if (this._currentWindowIdRef === currentWindowId) {
        return;
      }
      this._currentWindowIdRef = currentWindowId;
      this.syncCurrentWindow();
      this.emit(context.constants.applicationEvents.currentWindowChanged);
    }

    /**
     * Display this application current window
     * @returns {null|VMApplication}
     */
    syncCurrentWindow() {
      if (!this._application) {
        return null;
      }

      if (this._application.isProcessing()) {
        // if application is processing, check if app is waiting for a child app
        const sessionNavigationManager = context.SessionService.getCurrent()?.getNavigationManager();
        const childWaitingApp = sessionNavigationManager.getAwaitedApplication(this._application);
        if (childWaitingApp) {
          childWaitingApp.getUI().syncCurrentWindow();
          return childWaitingApp;
        }
      }

      const session = this._application.getSession();
      const model = this._application.model;
      const UINode = model?.getNode(0);
      const UIController = UINode?.getController();
      const UIWidget = UIController?.getWidget();
      const children = UIWidget?.getChildren();

      let currentWin = children && this._currentWindowIdRef;
      const matchingChild = children.find((win) => win._auiTag === this._currentWindowIdRef);
      // If no window has been found, return the traditional window container
      const traditionalContainer = children.find((win) => win.hasClass("gbc_TraditionalContainerWindow"));
      // It need to have a current window ref and that the ui widget have children
      currentWin = currentWin && (matchingChild || traditionalContainer);

      if (!currentWin) {
        return null;
      }
      const currentWinNode = model && model.getNode(currentWin._auiTag);
      // before displaying window we display its corresponding application
      session.getWidget().setCurrentWidget(currentWin.getApplicationWidget());
      context.HostService.setDisplayedWindowNode(currentWinNode);

      gbc.HostService.updateDisplay(); // re-align everything in window

      return this._application;
    }
  }
  cls.UIApplicationService = UIApplicationService;
  cls.ApplicationServiceFactory.register("UI", cls.UIApplicationService);
});
